import { AxiosResponse } from 'axios';
import { NextApiRequestQuery } from 'next/dist/server/api-utils';
import { FormResponseData } from './user.service';
import {
    SubsidyAdditionalDataDocumentApiInterface,
    SubsidyAddLegalPersonApiInterface,
    SubsidyAddLegalPersonExcelApiInterface,
    SubsidyCreateApiInterface,
} from 'submodules/api_middleware';
import { ApiResponseInterface } from 'utils/helpers/browser/getResponse';
import fetcher, {
    fetcherFull,
    MethodEnum,
} from 'utils/helpers/browser/newFetcher';
import { QueryRequestInterface } from 'submodules/api_middleware/src/interfaces/InternalInterfaces';

export const getSubisdyList = async (
    query: NextApiRequestQuery
): Promise<AxiosResponse> => {
    return await fetcher(`/api/subsidy/list`, MethodEnum.GET, query);
};

export const getSubisdyListFull = async (
    query: NextApiRequestQuery
): Promise<AxiosResponse> => {
    return await fetcherFull(`/api/subsidy/list`, MethodEnum.GET, query);
};

export const createSubsidy = async (
    data: SubsidyCreateApiInterface
): Promise<FormResponseData> => {
    return await fetcher<ApiResponseInterface>(
        `/api/subsidy/create`,
        MethodEnum.POST,
        null,
        { data }
    );
};

export const editSubsidy = async (
    subsidyId: number,
    data: SubsidyCreateApiInterface
): Promise<ApiResponseInterface> => {
    return await fetcher<ApiResponseInterface>(
        `/api/subsidy/edit`,
        MethodEnum.PUT,
        null,
        { subsidyId, data }
    );
};

export const deleteSubsidy = async (
    subsidyId: number
): Promise<ApiResponseInterface> => {
    return await fetcher<ApiResponseInterface>(
        `/api/subsidy/delete`,
        MethodEnum.DELETE,
        null,
        { subsidyId }
    );
};

export const createAdditionalDataForDocument = async (
    subsidyId: number,
    data: SubsidyAdditionalDataDocumentApiInterface
): Promise<ApiResponseInterface> => {
    return await fetcher<ApiResponseInterface>(
        `/api/subsidy/${subsidyId}/document/additional-data-create`,
        MethodEnum.POST,
        null,
        { subsidyId, data }
    );
};

export const editAdditionalDataForDocument = async (
    subsidyId: number,
    additionalDataId: number,
    data: SubsidyAdditionalDataDocumentApiInterface
): Promise<ApiResponseInterface> => {
    return await fetcher<ApiResponseInterface>(
        `/api/subsidy/${subsidyId}/document/${additionalDataId}/additional-data-edit`,
        MethodEnum.PUT,
        null,
        { data }
    );
};

export const deleteAdditionalDataForDocument = async (
    subsidyId: number,
    additionalDataId: number
): Promise<ApiResponseInterface> => {
    return await fetcher<ApiResponseInterface>(
        `/api/subsidy/${subsidyId}/document/${additionalDataId}/additional-data-delete`,
        MethodEnum.DELETE
    );
};

export const addSubsidyFile = async (
    subsidyId: number,
    documentId: number,
    document: File
): Promise<FormResponseData> => {
    const formData = new FormData();
    formData.append('file', document);

    return await fetcher<FormResponseData>(
        `/api/subsidy/${subsidyId}/document/${documentId}/add_file`,
        MethodEnum.POST,
        null,
        formData
    );
};

export const deleteSubsidyFile = async (
    subsidyId: number,
    documentId: number
): Promise<FormResponseData> => {
    return await fetcher<FormResponseData>(
        `/api/subsidy/${subsidyId}/document/${documentId}/delete_file`,
        MethodEnum.DELETE
    );
};

export const getSubsidyLegalPersonList = async (
    subsidyId: number,
    query: QueryRequestInterface
): Promise<AxiosResponse> => {
    return await fetcherFull(
        `/api/subsidy/${subsidyId}/legal_person/list`,
        MethodEnum.GET,
        { ...query, subsidyId }
    );
};

export const addLegalPersonToSubsidy = async (
    subsidyId: number,
    legalPersonId: number,
    data: SubsidyAddLegalPersonApiInterface
): Promise<FormResponseData> => {
    return await fetcher<ApiResponseInterface>(
        `/api/subsidy/${subsidyId}/legal_person/${legalPersonId}/add`,
        MethodEnum.POST,
        null,
        { data }
    );
};

export const listLegalPersons = async (
    query: QueryRequestInterface
): Promise<FormResponseData> => {
    return await fetcher<ApiResponseInterface>(
        `/api/subsidy/legal_persons`,
        MethodEnum.GET,
        query
    );
};

export const listSubisdyLegalPersons = async (
    subsidyId: number,
    query: QueryRequestInterface
): Promise<FormResponseData> => {
    return await fetcher<ApiResponseInterface>(
        `/api/subsidy/${subsidyId}/legal_persons`,
        MethodEnum.GET,
        query
    );
};

export const cancelLegalPersonSubsidy = async (
    subsidyId: number,
    legalPersonId: number
): Promise<FormResponseData> => {
    return await fetcher<ApiResponseInterface>(
        `/api/subsidy/${subsidyId}/legal_person/${legalPersonId}/cancel`,
        MethodEnum.POST
    );
};

export const deleteLegalPersonSubsidy = async (
    subsidyId: number,
    legalPersonId: number
): Promise<FormResponseData> => {
    return await fetcher<ApiResponseInterface>(
        `/api/subsidy/${subsidyId}/legal_person/${legalPersonId}/delete`,
        MethodEnum.DELETE
    );
};

export const updateLegalPersonToSubsidy = async (
    subsidyId: number,
    legalPersonId: number,
    data: SubsidyAddLegalPersonApiInterface
): Promise<FormResponseData> => {
    return await fetcher<ApiResponseInterface>(
        `/api/subsidy/${subsidyId}/legal_person/${legalPersonId}/update`,
        MethodEnum.PUT,
        null,
        { data }
    );
};

export const addLegalPersonToSubsidyExcel = async (
    subsidyId: number,
    data: SubsidyAddLegalPersonExcelApiInterface
): Promise<FormResponseData> => {
    const formData = new FormData();
    formData.append('file', data.file);
    formData.append('id_column', data.id_column);
    formData.append('amount_column', data.amount_column);
    formData.append('start_from_row', String(data.start_from_row));
    formData.append('type', data.type);

    return await fetcher<ApiResponseInterface>(
        `/api/subsidy/${subsidyId}/legal_person/excel`,
        MethodEnum.POST,
        null,
        formData
    );
};
